/**
 * Relationship Service - Client-side management of monitoring relationships
 */

import { apiBase } from "../api-config";

// API base URL (dynamic for dev vs prod)
const API_BASE = apiBase;

// Types for relationships
export interface MonitoredUser {
  userId: string;
  displayName: string;
  email?: string;
  relationshipType: "monitor" | "clinician";
  acceptedAt?: string;
  lastViewedAt?: string;
  lastSyncedAt?: string; // When data was last synced locally
  lastGlucose?: number; // Most recent glucose reading (client-computed)
  entriesCount?: number; // Number of entries (client-computed)
}

export interface Monitor {
  userId: string;
  displayName: string;
  email: string;
  relationshipType: "monitor" | "clinician";
}

export interface Invitation {
  inviteCode: string;
  allowedRoles: string[];
  expiresAt: string;
  isSingleUse: boolean;
  useCount: number;
  maxUses: number;
  inviteUrl: string;
  isExpired: boolean;
  isFullyUsed: boolean;
}

export interface InviteValidation {
  diabeticDisplayName: string;
  allowedRoles: string[];
  expiresAt: string;
}

export interface AcceptInvitationResult {
  success: boolean;
  relationshipType: string;
  diabeticDisplayName: string;
  diabeticUserId: string;
}

export interface CreateInvitationRequest {
  allowedRoles: string[];
  expiryDays?: number;
  isSingleUse?: boolean;
}

class RelationshipService {
  // ============================================
  // Invitations
  // ============================================

  /**
   * Create a new share invitation
   */
  async createInvitation(
    request: CreateInvitationRequest,
  ): Promise<Invitation> {
    const response = await fetch(`${API_BASE}/api/relationships/invite`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        allowedRoles: request.allowedRoles,
        expiryDays: request.expiryDays || 7,
        isSingleUse: request.isSingleUse ?? true,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create invitation");
    }

    return response.json();
  }

  /**
   * Get all invitations created by the current user
   */
  async getMyInvitations(): Promise<Invitation[]> {
    const response = await fetch(`${API_BASE}/api/relationships/invite`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get invitations");
    }

    return response.json();
  }

  /**
   * Validate an invitation code (public - for invite acceptance page)
   */
  async validateInvitation(code: string): Promise<InviteValidation | null> {
    const response = await fetch(
      `${API_BASE}/api/relationships/invite/${code}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const error = await response.json();
      throw new Error(error.error || "Failed to validate invitation");
    }

    return response.json();
  }

  /**
   * Accept an invitation
   */
  async acceptInvitation(
    code: string,
    chosenRole: "monitor" | "clinician",
  ): Promise<AcceptInvitationResult> {
    const response = await fetch(
      `${API_BASE}/api/relationships/invite/${code}/accept`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ chosenRole }),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to accept invitation");
    }

    return response.json();
  }

  /**
   * Delete an invitation
   */
  async deleteInvitation(code: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/relationships/invite/${code}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete invitation");
    }
  }

  // ============================================
  // Monitoring Relationships
  // ============================================

  /**
   * Get users I am monitoring
   */
  async getMonitoredUsers(): Promise<MonitoredUser[]> {
    const response = await fetch(`${API_BASE}/api/relationships/monitoring`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get monitored users");
    }

    return response.json();
  }

  /**
   * Get users who are monitoring me
   */
  async getMyMonitors(): Promise<Monitor[]> {
    const response = await fetch(`${API_BASE}/api/relationships/monitors`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get monitors");
    }

    return response.json();
  }

  /**
   * Revoke a monitor's access to my data
   */
  async revokeMonitorAccess(monitorUserId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/relationships/monitors/${monitorUserId}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to revoke access");
    }
  }

  /**
   * Stop monitoring a user
   */
  async stopMonitoring(diabeticUserId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/relationships/monitoring/${diabeticUserId}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to stop monitoring");
    }
  }

  /**
   * Update last viewed time for a monitored user (clinicians)
   */
  async updateLastViewed(diabeticUserId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/relationships/monitoring/${diabeticUserId}/viewed`,
      {
        method: "POST",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update last viewed");
    }
  }

  // ============================================
  // Clinic Sharing
  // ============================================

  /**
   * Get available clinics to share with
   */
  async getAvailableClinics(): Promise<ClinicSummary[]> {
    const response = await fetch(
      `${API_BASE}/api/relationships/clinics/available`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to get available clinics");
    }

    return response.json();
  }

  /**
   * Get clinics I'm sharing with
   */
  async getMyClinicShares(): Promise<ClinicShare[]> {
    const response = await fetch(
      `${API_BASE}/api/relationships/clinics/shared`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to get clinic shares");
    }

    return response.json();
  }

  /**
   * Share data with a clinic
   */
  async shareWithClinic(clinicId: string): Promise<ClinicShare> {
    const response = await fetch(
      `${API_BASE}/api/relationships/clinics/${clinicId}/share`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Failed to share with clinic");
    }

    return response.json();
  }

  /**
   * Revoke sharing with a clinic
   */
  async revokeClinicShare(clinicId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/api/relationships/clinics/${clinicId}/share`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to revoke clinic share");
    }
  }
}

// Clinic sharing types
export interface ClinicSummary {
  clinicId: string;
  name: string;
  description?: string;
  isSharing: boolean;
}

export interface ClinicShare {
  clinicId: string;
  clinicName: string;
  sharedAt: string;
}

// Export singleton instance
export const relationshipService = new RelationshipService();
